// 051 Recién nacido, egresos (RN y materno) y anticoncepción.
// El recién nacido no tiene Patient propio: sus datos se registran en la HC de la madre con `category` = `recien-nacido`
// (SISTEMA_051_SUJETO) para no confundirlos con datos maternos; por la misma razón sus diagnósticos van como Observation
// (valueCodeableConcept con CIE-10) y NO como Condition (una Condition de la madre diría que ELLA tiene la enfermedad).
// Apgar 1/5/10: LOINC 9272-6 / 9274-2 / 9271-8 (valueInteger); peso al nacer 8339-4; perímetro cefálico 9843-4; EG 11884-4.
import type { Observation } from '@medplum/fhirtypes';
import { codeDeDiagnostico } from '../SelectorDiagnosticoCie10';
import {
  ATENDIO_RN_OPCIONES,
  METODO_ACCEDIDO_OPCIONES,
  METODO_ANTICONCEPCION_OPCIONES,
  REANIMACION_RN_OPCIONES,
  TAMIZAJE_AUDITIVO_OPCIONES,
  type EstadoEgresoMaterno051,
  type EstadoEgresoRn051,
  type EstadoRecienNacido051,
} from '../bloques051PartogramaDatos';
import {
  CATEGORIA_RECIEN_NACIDO,
  cFecha,
  cHora,
  cMarca,
  cNumero,
  cOpcion,
  cSiNo,
  cTexto,
  esFechaIso,
  LOINC_051,
  local,
  Seccion051,
  U,
  valorNumerico,
  valorOpcion,
  type Comp,
  type Contexto051,
} from './base';

const R = 'formularios.f051.recienNacido';
const ER = 'formularios.f051.egresoRn';
const EM = 'formularios.f051.egresoMaterno';

export function agregarRecienNacido(ctx: Contexto051, e: EstadoRecienNacido051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('recien-nacido', 'Recién nacido'), CATEGORIA_RECIEN_NACIDO);
  const op = (clave: string, v: string): Comp => cOpcion(local(clave, t(`${R}.${clave}`)), v, v ? t(`${R}.${clave}Opciones.${v}`) : '');
  const sn = (clave: string, v: string): Comp => cSiNo(local(clave, t(`${R}.${clave}`)), v);

  if (e.sexo) s.grupo(local('sexo-recien-nacido', t(`${R}.sexo`)), [], { valueCodeableConcept: valorOpcion(e.sexo, t(`${R}.sexoOpciones.${e.sexo}`)) });

  s.grupo(LOINC_051.pesoAlNacer, [op('pesoAlNacerAlerta', e.pesoAlNacerAlerta)], valorNumerico(e.pesoAlNacer, U.g) ?? {});
  const longitud = valorNumerico(e.longitud, U.cm);
  if (longitud) s.grupo(local('longitud-al-nacer', t(`${R}.longitud`)), [], longitud);
  const perimetro = valorNumerico(e.perimetroCefalico, U.cm);
  if (perimetro) s.grupo(LOINC_051.perimetroCefalico, [], perimetro);

  s.grupo(
    LOINC_051.edadGestacional,
    [cNumero(local('eg-dias', t(`${R}.edadGestacionalDias`)), e.edadGestacionalDias, U.dias), op('egFuente', e.egFuente), op('pesoEg', e.pesoEg)],
    valorNumerico(e.edadGestacionalSemanas, U.semanas) ?? {}
  );

  // Apgar: entero 0-10; algo distinto se conserva como texto. Si "no aplica" el de 10' no se guarda.
  const apgar = (code: typeof LOINC_051.apgar1, texto: string): void => {
    const x = texto.trim();
    if (!x) return;
    s.grupo(code, [], /^\d+$/.test(x) ? { valueInteger: Number(x) } : { valueString: x });
  };
  apgar(LOINC_051.apgar1, e.apgar1);
  apgar(LOINC_051.apgar5, e.apgar5);
  if (!e.apgarNoAplica) apgar(LOINC_051.apgar10, e.apgar10);
  else s.grupo(local('apgar-10-no-aplica', t(`${R}.apgarNoAplica`)), [], { valueBoolean: true });

  s.grupo(
    local('reanimacion-recien-nacido', t(`${R}.reanimacion.titulo`)),
    REANIMACION_RN_OPCIONES.map((c) => cSiNo(local(`reanimacion-${c}`, t(`${R}.reanimacion.opciones.${c}`)), e.reanimacion[c]))
  );

  s.grupo(local('atencion-recien-nacido', 'Atención del recién nacido'), [
    sn('falleceEnLugarParto', e.falleceEnLugarParto),
    ...ATENDIO_RN_OPCIONES.map((c) => cMarca(local(`atendio-parto-${c}`, `${t(`${R}.atendioParto.titulo`)} — ${t(`${R}.atendio.opciones.${c}`)}`), e.atendioParto[c])),
    ...ATENDIO_RN_OPCIONES.map((c) => cMarca(local(`atendio-neonato-${c}`, `${t(`${R}.atendioNeonato.titulo`)} — ${t(`${R}.atendio.opciones.${c}`)}`), e.atendioNeonato[c])),
    op('traslado', e.traslado),
    cTexto(local('tipificacion-rn', t(`${R}.tipificacionRn`)), e.tipificacionRn),
    sn('vitaminaK', e.vitaminaK),
    sn('profilaxisOcular', e.profilaxisOcular),
    sn('metodoCanguroAmerita', e.metodoCanguroAmerita),
    // "Se aplica" cuelga de "Amerita": si no amerita, no hay nada que aplicar.
    e.metodoCanguroAmerita === 'no' ? undefined : sn('metodoCanguroSeAplica', e.metodoCanguroSeAplica),
    sn('examenFisicoNormal', e.examenFisicoNormal),
    sn('vacunaHepatitisBDosis0', e.vacunaHepatitisBDosis0),
    sn('apegoInmediato', e.apegoInmediato),
    sn('meconio1erDia', e.meconio1erDia),
    sn('lactanciaInmediata', e.lactanciaInmediata),
  ]);

  if (e.defectosCongenitos) {
    s.grupo(
      local('defectos-congenitos', t(`${R}.defectosCongenitos`)),
      [e.defectosCongenitos === 'no' ? undefined : cTexto(local('defectos-congenitos-cie10', t(`${R}.defectosCongenitosCie10`)), e.defectosCongenitosCie10)],
      { valueCodeableConcept: valorOpcion(e.defectosCongenitos, t(`${R}.defectosCongenitosOpciones.${e.defectosCongenitos}`)) }
    );
  }

  if (e.enfermedades) {
    s.grupo(local('enfermedades-recien-nacido', t(`${R}.enfermedades`)), [], { valueCodeableConcept: valorOpcion(e.enfermedades, t(`${R}.enfermedadesOpciones.${e.enfermedades}`)) });
  }
  if (e.enfermedades !== 'ninguna') {
    for (const d of e.enfermedadesDx) {
      if (!d.texto.trim()) continue;
      s.grupo(local('diagnostico-recien-nacido', 'Diagnóstico del recién nacido (CIE-10)'), [], { valueCodeableConcept: codeDeDiagnostico(d) });
    }
  }

  if (e.tamizajeMetabolico) s.grupo(local('tamizaje-metabolico', t(`${R}.tamizajeMetabolico`)), [], { valueCodeableConcept: valorOpcion(e.tamizajeMetabolico, t(`${R}.tamizajeMetabolicoOpciones.${e.tamizajeMetabolico}`)) });
  s.grupo(
    local('tamizaje-auditivo', t(`${R}.tamizajeAuditivo.titulo`)),
    TAMIZAJE_AUDITIVO_OPCIONES.map((c) => cMarca(local(`tamizaje-auditivo-${c}`, t(`${R}.tamizajeAuditivo.opciones.${c}`)), e.tamizajeAuditivo[c]))
  );

  s.cerrar();
}

export function agregarEgresos(ctx: Contexto051, rn: EstadoEgresoRn051, materno: EstadoEgresoMaterno051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('egresos', 'Egresos (recién nacido y materno)'));
  const efectivoEn = (fecha: string): Partial<Observation> => (esFechaIso(fecha) ? { effectiveDateTime: fecha.trim() } : {});

  // --- Egreso del recién nacido (category recien-nacido) ---
  s.grupo(
    local('egreso-recien-nacido', 'Egreso del recién nacido'),
    [
      cOpcion(local('condicion-egreso-rn', t(`${ER}.condicion`)), rn.condicion, rn.condicion ? t(`${ER}.condicionOpciones.${rn.condicion}`) : ''),
      cFecha(local('fecha-egreso-rn', t(`${ER}.fecha`)), rn.fecha),
      cHora(local('hora-egreso-rn', t(`${ER}.hora`)), rn.hora),
      cSiNo(local('fallece-durante-referencia', t(`${ER}.falleceDuranteReferencia`)), rn.falleceDuranteReferencia),
      cSiNo(local('fallece-en-lugar-referencia', t(`${ER}.falleceEnLugarReferencia`)), rn.falleceEnLugarReferencia),
      cTexto(local('establecimiento-egreso-rn', t(`${ER}.nombreEstablecimiento`)), rn.nombreEstablecimiento),
      cOpcion(local('alimento-al-alta', t(`${ER}.alimentoAlAlta`)), rn.alimentoAlAlta, rn.alimentoAlAlta ? t(`${ER}.alimentoAlAltaOpciones.${rn.alimentoAlAlta}`) : ''),
      cSiNo(local('decubito-supino', t(`${ER}.decubitoSupino`)), rn.decubitoSupino),
      cMarca(local('edad-menor-1-dia', t(`${ER}.edadMenor1Dia`)), rn.edadMenor1Dia),
      rn.edadMenor1Dia ? undefined : cNumero(local('edad-dias-completos', t(`${ER}.edadDiasCompletos`)), rn.edadDiasCompletos, U.dias),
      cSiNo(local('bcg', t(`${ER}.bcg`)), rn.bcg),
      cTexto(local('certificado-cc-hcu', t(`${ER}.certificadoCcHcu`)), rn.certificadoCcHcu),
    ],
    { category: [CATEGORIA_RECIEN_NACIDO], ...efectivoEn(rn.fecha) }
  );
  const pesoEgreso = valorNumerico(rn.pesoAlEgreso, U.g);
  // Código propio (no LOINC 29463-7): ese código lo leen las curvas de crecimiento de la madre (por paciente, sin filtrar category).
  if (pesoEgreso) s.grupo(local('peso-egreso-recien-nacido', t(`${ER}.pesoAlEgreso`)), [], { ...pesoEgreso, category: [CATEGORIA_RECIEN_NACIDO], ...efectivoEn(rn.fecha) });

  // --- Egreso materno ---
  s.grupo(
    local('egreso-materno', 'Egreso materno'),
    [
      cOpcion(local('condicion-egreso-materno', t(`${EM}.condicion`)), materno.condicion, materno.condicion ? t(`${EM}.condicionOpciones.${materno.condicion}`) : ''),
      cFecha(local('fecha-egreso-materno', t(`${EM}.fecha`)), materno.fecha),
      cHora(local('hora-egreso-materno', t(`${EM}.hora`)), materno.hora),
      cSiNo(local('fallece-durante-referencia-materno', t(`${EM}.falleceDuranteReferencia`)), materno.falleceDuranteReferencia),
      cSiNo(local('fallece-en-lugar-referencia-materno', t(`${EM}.falleceEnLugarReferencia`)), materno.falleceEnLugarReferencia),
      cNumero(local('dias-completos-desde-parto', t(`${EM}.diasCompletosDesdeParto`)), materno.diasCompletosDesdeParto, U.dias),
      cTexto(local('establecimiento-egreso-materno', t(`${EM}.nombreEstablecimientoResponsable`)), materno.nombreEstablecimientoResponsable),
    ],
    efectivoEn(materno.fecha)
  );
  s.cerrar();
}

export function agregarAnticoncepcion(ctx: Contexto051, m: EstadoEgresoMaterno051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('anticoncepcion', 'Anticoncepción'));
  // Sin asesoría ("No") no hay método que registrar: no se guarda un dato huérfano.
  const abierto = m.anticoncepcionAsesoria !== 'no';
  s.grupo(
    local('asesoria-anticoncepcion', t(`${EM}.anticoncepcionAsesoria`)),
    abierto
      ? [
          ...METODO_ANTICONCEPCION_OPCIONES.map((c) => cMarca(local(`metodo-preferido-${c}`, `${t(`${EM}.anticoncepcionPreferido`)} — ${t(`${EM}.metodoOpciones.${c}`)}`), m.anticoncepcionPreferido[c])),
          ...METODO_ACCEDIDO_OPCIONES.map((c) => cMarca(local(`metodo-accedido-${c}`, `${t(`${EM}.anticoncepcionAccedido`)} — ${t(`${EM}.metodoOpciones.${c}`)}`), m.anticoncepcionAccedido[c])),
        ]
      : [],
    m.anticoncepcionAsesoria === 'si' ? { valueBoolean: true } : m.anticoncepcionAsesoria === 'no' ? { valueBoolean: false } : {}
  );
  s.cerrar();
}
